import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Custom React Hook to detect if text-overflow: ellipsis is active on an element.
 * Requires the element to have white-space: nowrap; and overflow: hidden; applied.
 *
 * @returns {[boolean, React.RefObject<HTMLElement>]} A tuple where:
 * - The first element is a boolean indicating if ellipsis is active.
 * - The second element is a ref to be attached to the DOM element you want to monitor.
 */
function useEllipsisActive(): [boolean, React.RefObject<HTMLElement>] {
  const elementRef = useRef<HTMLElement>(null);
  const [isEllipsisActive, setIsEllipsisActive] = useState<boolean>(false);

  const checkEllipsis = useCallback(() => {
    if (elementRef.current) {
      setIsEllipsisActive(elementRef.current.offsetWidth < elementRef.current.scrollWidth);
    }
  }, []);

  useEffect(() => {
    checkEllipsis();
    const resizeObserver = new ResizeObserver((entries: ResizeObserverEntry[]) => {
      checkEllipsis();
    });

    if (elementRef.current) {
      resizeObserver.observe(elementRef.current);
    }

    return () => {
      if (elementRef.current) {
        // eslint-disable-next-line react-hooks/exhaustive-deps
        resizeObserver.unobserve(elementRef.current as unknown as Element);
      }
      resizeObserver.disconnect();
    };
  }, [checkEllipsis]);

  // @ts-ignore
  return [isEllipsisActive, elementRef];
}

export default useEllipsisActive;
